<template>
    <painel titulo="Listar Páginas" icone="" :refreshFunction="carregarPaginas">
        <tabela
            :data="paginasFiltradas"
            :first="first"
            :rows="rows"
            :lazy="true"
            :loading="loading"
            stateKey="dt-landingPage"
            :paginator="true"
            :rowsPerPageOptions="[5, 10, 20, 50]"
            :totalRecords="totalPaginasFiltradas"
            :mostrarPesquisa="false"
            @update:rows="onRowsChange"
            @update:first="onFirstChange"
            @sort="onSort"
            :sortField="sortField"
            :sortOrder="sortOrder"
        >
            <template #filtros>
                <div class="flex flex-wrap items-end bg-gray-10 rounded-md">
                    <div class="flex-grow basis-[250px] mr-2 mb-2">
                        <span class="p-input-icon-left w-full">
                            <i class="pi pi-search" />
                            <InputText v-model="filtro.nome" placeholder="Buscar páginas..." class="w-full" />
                        </span>
                    </div>
                    <div class="flex-grow basis-[180px] mr-2 mb-2">
                        <Dropdown
                            v-model="filtro.status"
                            :options="statusOptions"
                            optionLabel="label"
                            optionValue="value"
                            placeholder="Todos os status"
                            class="w-full"
                        />
                    </div>
                    <div class="flex-grow basis-[150px] mr-2 mb-2">
                        <Calendar v-model="filtro.dataInicio" placeholder="Data início" class="w-full" showIcon />
                    </div>
                    <div class="flex-grow basis-[150px] mr-2 mb-2">
                        <Calendar v-model="filtro.dataFim" placeholder="Data fim" class="w-full" showIcon />
                    </div>
                    <div class="flex-none aspect-square mr-2 mb-2">
                        <Button icon="pi pi-filter" class="p-button-sm w-full h-full" @click="filtrar" />
                    </div>
                    <div class="flex-none aspect-square mb-2">
                        <Button icon="pi pi-times" class="p-button-secondary p-button-sm w-full h-full" @click="limparFiltro" />
                    </div>
                </div>
            </template>
            <template #conteudo>
                <Column field="nome" header="Nome" :sortable="true" />
                <Column field="dataCriacao" header="Data de Criação" :sortable="true" />
                <Column field="ultimaAtualizacao" header="Última Atualização" :sortable="true" />
                <Column header="Status">
                    <template #body="slotProps">
                        <status :status="slotProps.data.status" />
                    </template>
                </Column>
                <Column headerStyle="width: 10em">
                    <template #header>
                        <span>
                            Ações
                            <i
                                class="pi pi-question-circle"
                                v-tooltip.top="{
                                    value: `Botão Editar: Edita a Landing Page.<br />
                                            Botão Visualizar: Visualiza a Landing Page conforme o seu status.<br />
                                            Botão de Desativar/Ativar: Desativa a Landing Page publicada / Ativa novamente.`,
                                    escape: false,
                                }"
                            ></i>
                        </span>
                    </template>
                    <template #body="slotProps">
                        <Button icon="pi pi-pencil" class="p-button-sm p-button-text" @click="editar(slotProps.data)" />
                        <Button
                            icon="pi pi-eye"
                            class="p-button-sm p-button-text"
                            :disabled="slotProps.data.status !== 'Publicado' && slotProps.data.status !== 'Rascunho'"
                            @click="visualizar(slotProps.data)"
                        />
                        <Button
                            v-if="slotProps.data.status === 'Desativado' || slotProps.data.status === 'Publicado'"
                            :icon="slotProps.data.status === 'Desativado' ? 'pi pi-check-circle' : 'pi pi-times-circle'"
                            :class="['p-button-sm', slotProps.data.status === 'Desativado' ? 'p-button-success' : 'p-button-danger']"
                            @click="alternarStatus(slotProps.data)"
                        />
                    </template>
                </Column>
            </template>
        </tabela>
    </painel>
    <router-view />
</template>

<script setup>
import { ref, reactive, onMounted, watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { useToast } from 'primevue/usetoast';
import EventosServices from './services';
import { tryCatch } from '@/utils/tryCatch';
import { useEditorLandingPage } from '@/composables/useEditorLandingPage';

const filtro = reactive({
    nome: '',
    status: null,
    dataInicio: null,
    dataFim: null,
});

const stateKey = 'dt-landingPage';
const defaultRows = 50;
const defaultFirst = 0;

const getInitialState = () => {
    try {
        const storedStateJSON = sessionStorage.getItem(`p-datatable-session-${stateKey}`);
        if (!storedStateJSON) return { first: defaultFirst, rows: defaultRows, sortField: 'ultimaAtualizacao', sortOrder: -1 };

        const storedState = JSON.parse(storedStateJSON);
        return {
            first: storedState.first ?? defaultFirst,
            rows: storedState.rows ?? defaultRows,
            sortField: storedState.sortField ?? 'ultimaAtualizacao',
            sortOrder: storedState.sortOrder ?? -1,
        };
    } catch (e) {
        console.error('Erro ao restaurar o estado da tabela:', e);
        return { first: defaultFirst, rows: defaultRows, sortField: 'ultimaAtualizacao', sortOrder: -1 };
    }
};

const initialState = getInitialState();

const paginas = ref([]);
const paginasFiltradas = ref([]);
const totalPaginasFiltradas = ref(0);
const erroMensagem = ref('');
const loading = ref(false);
const first = ref(initialState.first);
const rows = ref(initialState.rows);
const sortField = ref(initialState.sortField);
const sortOrder = ref(initialState.sortOrder);

const statusOptions = ref([
    { label: 'Todos os status', value: null },
    { label: 'Rascunho', value: 'Rascunho' },
    { label: 'Publicado', value: 'Publicado' },
    { label: 'Desativado', value: 'Desativado' },
]);

const router = useRouter();
const route = useRoute();
const store = useStore();
const toast = useToast();
const { abrirPreview } = useEditorLandingPage();

const aplicarFiltro = () => {
    const nomeFiltro = filtro.nome?.toLowerCase() || '';

    let resultadoFiltrado = paginas.value.filter((p) => {
        const data = p.dataCriacaoObj;
        const existeNomeFiltrado = !nomeFiltro || p.nome.toLowerCase().includes(nomeFiltro);
        const existeStatus = !filtro.status || p.status === filtro.status;
        const existeInicio = !filtro.dataInicio || (data && data >= filtro.dataInicio);
        const existeFim = !filtro.dataFim || (data && data <= filtro.dataFim);

        return existeNomeFiltrado && existeStatus && existeInicio && existeFim;
    });

    // Ordenação
    if (sortField.value) {
        resultadoFiltrado.sort((a, b) => {
            const fieldA = a[sortField.value];
            const fieldB = b[sortField.value];
            let comparison = 0;

            if (fieldA instanceof Date && fieldB instanceof Date) {
                comparison = fieldA.getTime() - fieldB.getTime();
            } else if (typeof fieldA === 'string' && typeof fieldB === 'string') {
                comparison = fieldA.localeCompare(fieldB);
            } else {
                if (fieldA < fieldB) comparison = -1;
                if (fieldA > fieldB) comparison = 1;
            }

            return comparison * (sortOrder.value || 1);
        });
    }

    totalPaginasFiltradas.value = resultadoFiltrado.length;
    paginasFiltradas.value = resultadoFiltrado.slice(first.value, first.value + rows.value);
};

const exibirErro = (titulo, mensagem) => {
    toast.add({
        severity: 'error',
        summary: titulo,
        detail: mensagem,
        life: 5000,
    });
};

const deveSubstituirPagina = (nova, existente) => {
    const novaEhAtual = !!nova.versaoAtual;
    const existenteEhAtual = !!existente.versaoAtual;

    if (novaEhAtual !== existenteEhAtual) {
        return novaEhAtual;
    }

    if (nova.dataAtualizacaoObj?.getTime() !== existente.dataAtualizacaoObj?.getTime()) {
        return nova.dataAtualizacaoObj > existente.dataAtualizacaoObj;
    }

    const novaEhPublicada = nova.status === 'Publicado';
    const existenteEhPublicada = existente.status === 'Publicado';

    return novaEhPublicada && !existenteEhPublicada;
};

const eventosMap = ref(new Map());

const carregarEventos = async () => {
    const { data: { data: eventosData } = {}, error } = await tryCatch(EventosServices.obterTodos({}));
    if (!error && eventosData) {
        eventosMap.value = new Map(eventosData.map((evento) => [evento.eventoId, evento]));
    }
};

const mapearPagina = (pagina) => {
    const { metadados } = pagina;
    const formatarData = (d) => (d ? new Date(d).toLocaleDateString('pt-BR') : null);
    const dataCriacaoObj = metadados.dataCriacao ? new Date(metadados.dataCriacao) : null;
    const dataAtualizacaoObj = metadados.dataAtualizacao ? new Date(metadados.dataAtualizacao) : null;
    const eventoCorrespondente = eventosMap.value.get(metadados.eventoId);

    return {
        nome: eventoCorrespondente?.nome || metadados.titulo || 'Sem título',
        status: metadados.status || 'Rascunho',
        dataCriacaoObj: dataCriacaoObj,
        ultimaAtualizacaoObj: dataAtualizacaoObj,
        eventoId: metadados.eventoId,
        landingPageId: metadados.landingPageId,
        link: metadados.link || '',
        urlPersonalizada: metadados.urlPersonalizada || '',
        dataCriacao: formatarData(metadados.dataCriacao) || '',
        ultimaAtualizacao: formatarData(metadados.dataAtualizacao) || '',
        dataAtualizacaoObj: dataAtualizacaoObj, // Manter para compatibilidade, se necessário
        versaoAtual: metadados.versaoAtual,
    };
};

const carregarPaginas = async () => {
    loading.value = true;
    erroMensagem.value = '';

    await carregarEventos();

    const { data: { data } = {}, error } = await tryCatch(EventosServices.obterTodasLandingPagesPersistidas({ versaoAtual: true }));

    if (error) {
        exibirErro('Erro ao carregar páginas', 'Não foi possível buscar os dados. Tente novamente.');
        paginas.value = [];
        loading.value = false;
        return;
    }

    const paginasMapeadas = data.map(mapearPagina);

    const paginasAgrupadas = paginasMapeadas.reduce((acc, pagina) => {
        const chave = `${pagina.eventoId}-${pagina.nome}`;
        const existente = acc[chave];

        if (!existente || deveSubstituirPagina(pagina, existente)) {
            acc[chave] = pagina;
        }

        return acc;
    }, {});

    paginas.value = Object.values(paginasAgrupadas);
    aplicarFiltro();
    loading.value = false;
};

const filtrar = () => {
    first.value = 0;
    aplicarFiltro();
};

const onRowsChange = (value) => {
    rows.value = value;
    aplicarFiltro();
};

const onFirstChange = (value) => {
    first.value = value;
    aplicarFiltro();
};

const onSort = (event) => {
    sortField.value = event.sortField;
    sortOrder.value = event.sortOrder;
    aplicarFiltro();
};

const limparFiltro = () => {
    Object.assign(filtro, {
        nome: '',
        status: null,
        dataInicio: null,
        dataFim: null,
    });
    filtrar();
};

const editar = (pagina) => {
    router.push({
        name: 'Checkins_Eventos_EditarLanding',
        params: {
            eventoId: pagina.eventoId,
            landingPageId: pagina.landingPageId,
        },
    });
};

const visualizar = (pagina) => {
    if (pagina.status === 'Publicado' && pagina.urlPersonalizada) {
        const baseUrl = window.RUNTIME_CONFIG?.LANDING_PAGE_PREVIEW_URL || process.env.VUE_APP_LANDING_PAGE_PREVIEW_URL || '';
        const url = `${baseUrl}/${pagina.urlPersonalizada}`;
        window.open(url, '_blank');
    } else if (pagina.status === 'Rascunho') {
        abrirPreview({ landingPageId: pagina.landingPageId });
    }
};

const alternarStatus = async (pagina) => {
    const novoStatus = pagina.status === 'Desativado' ? 'Publicado' : 'Desativado';
    const acao = novoStatus === 'Publicado' ? 'ativada' : 'desativada';

    try {
        const res = await EventosServices.desativarLandingPage(pagina.landingPageId);
        if (res.success === false) {
            throw new Error(res.errors.join(', '));
        }

        const paginaIndex = paginas.value.findIndex((p) => p.landingPageId === pagina.landingPageId);
        if (paginaIndex !== -1) {
            paginas.value[paginaIndex].status = novoStatus;
        }

        aplicarFiltro();

        toast.add({
            severity: 'success',
            summary: 'Sucesso',
            detail: `Página ${acao} com sucesso.`,
            life: 3000,
        });
    } catch (error) {
        console.error('Erro ao alternar status da página:', error);
        exibirErro('Erro ao Alterar Status', 'Não foi possível alterar o status da página. Tente novamente.');
    }
};

onMounted(() => {
    carregarPaginas();
});

watch(
    () => route,
    (to) => {
        if (to.name === 'Checkins_LandingPages' && store.getters.atualizar) {
            carregarPaginas();
            store.dispatch('setAtualizar', false);
        }
    },
    { deep: true, immediate: true }
);
</script>
